import { gsap } from 'gsap';
import type { ThreeStage } from '../../../three/ThreeStage';
import { BaseScene } from '../../../core/BaseScene';
import { CAPABILITIES, groups, type Capability, type Product } from '../../../presentation/catalogue';
import { draw, pulse } from '../../../components/svg';
import { words, revealWords, settleWords } from '../../../components/text';

const ROW_Y = 214;
const BASE_Y = 660;
const CAP_STEP = 1300 / CAPABILITIES.length;
const capX = (c: Capability): number => 800 + (CAPABILITIES.indexOf(c) - (CAPABILITIES.length - 1) / 2) * CAP_STEP;

/** Names short enough to sit side by side; hierarchy comes from size, not from wording. */
function label(product: Product): string {
  if (product.tier === 'specialisation') return product.name.replace(/^MINT /, '');
  return product.tier === 'flagship' ? product.name : product.shortName;
}

/**
 * 0 DIFFERENT PRODUCTS.
 * 1 SAME DNA. — the shared foundation rises
 * 2 Every product connects down into what it is built on — Never starting from zero.
 * 3 The next product connects to all of it at once
 */
export class SharedDnaScene extends BaseScene {
  public readonly stepCount = 4;

  private flowing = false;
  /** Enabled products, left to right, with one extra slot for whatever comes next. */
  private readonly row: Product[] = this.context.products;
  private readonly spacing = Math.min(170, 1360 / Math.max(this.row.length, 1));

  /** Products plus the unnamed NEXT slot, centred as one row. */
  private productX(i: number): number {
    return 800 + (i - this.row.length / 2) * this.spacing;
  }

  private link(x: number, c: Capability): string {
    const cx = capX(c);
    return `M${x} ${ROW_Y + 14} C${x} 440 ${cx} 440 ${cx} ${BASE_Y}`;
  }

  protected template(): string {
    const NEXT_X = this.productX(this.row.length);
    const row = this.row.map((product, i) => `
      <g class="dna-product dna-product--${product.tier}" style="--family:${groups[product.group].color}">
        <circle class="dna-product__dot" cx="${this.productX(i)}" cy="${ROW_Y}" r="${product.tier === 'flagship' ? 12 : product.tier === 'selected' ? 6 : 8}"/>
        <text class="dna-product__name" x="${this.productX(i)}" y="${ROW_Y - 28}" text-anchor="middle">${label(product)}</text>
      </g>`).join('');
    // The MINT family reads as one: a quiet bracket over MINT and its specialisations.
    const family = this.row.map((p, i) => ({ p, x: this.productX(i) })).filter(({ p }) => p.group === 'mint');
    const bracket = family.length > 1
      ? `<path class="dna-family" d="M${family[0].x} ${ROW_Y - 66} V${ROW_Y - 74} H${family[family.length - 1].x} V${ROW_Y - 66}"/>`
      : '';

    const links = this.row.flatMap((product, i) => product.capabilities.map((c) =>
      `<path class="dna-link draw" data-product="${i}" pathLength="1" d="${this.link(this.productX(i), c)}" style="--family:${groups[product.group].color}"/>`)).join('');
    const nextLinks = CAPABILITIES.map((c) => `<path class="dna-link dna-link--next draw" data-from="3" pathLength="1" d="${this.link(NEXT_X, c)}"/>`).join('');

    const base = CAPABILITIES.map((c) => `
      <g class="dna-cap">
        <rect x="${capX(c) - 78}" y="${BASE_Y}" width="156" height="64" rx="14"/>
        <text x="${capX(c)}" y="${BASE_Y + 38}" text-anchor="middle">${c.toUpperCase()}</text>
      </g>`).join('');

    return `
      <div class="scene-layout shared-dna">
        <svg class="diagram dna-diagram" viewBox="0 0 1600 900" role="img" aria-label="Products built on shared capabilities">
          <g class="dna-links">${links}${nextLinks}</g>
          <g class="dna-row">${bracket}${row}</g>
          <g class="dna-next" data-from="3">
            <circle class="dna-next__ring" cx="${NEXT_X}" cy="${ROW_Y}" r="16"/>
            <circle class="dna-next__dot" cx="${NEXT_X}" cy="${ROW_Y}" r="6"/>
            <text class="dna-next__label" x="${NEXT_X}" y="${ROW_Y - 28}" text-anchor="middle">NEXT</text>
          </g>
          <g class="dna-base" data-from="1">${base}</g>
          <g class="dna-pulses">${Array.from({ length: 12 }, () => '<circle class="dna-pulse" r="3.5"/>').join('')}</g>
        </svg>
        <div class="dna-statements">
          <h2 class="statement" data-from="0" data-until="1">${words('DIFFERENT PRODUCTS.')}</h2>
          <h2 class="statement statement--accent" data-from="1" data-until="1">${words('SAME DNA.')}</h2>
        </div>
        <p class="dna-line line-statement" data-from="2">${words('Never starting from zero.')}</p>
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    if (immediate || this.step !== 0) return;
    const tl = this.timeline();
    tl.fromTo(this.$$('.dna-product'), { opacity: 0, y: -14 }, { opacity: 1, y: 0, duration: 0.6, stagger: 0.08, ease: 'power3.out' }, 0.1);
    tl.add(revealWords(this.$('.dna-statements .statement')), 0.7);
  }

  protected onExit(): void {
    this.flowing = false;
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode('shared-dna', step, immediate);

    const forward = !immediate && step > previousStep;
    const tl = this.timeline();
    gsap.set(this.$$('.dna-product'), { opacity: 1, y: 0 });
    this.$$('.dna-statements .statement, .dna-line').forEach((s) => settleWords(s));

    if (forward && step === 1) {
      tl.add(revealWords(this.$$('.dna-statements .statement')[1]), 0.1);
      tl.fromTo(this.$$('.dna-cap'), { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.7, stagger: 0.07, ease: 'power3.out' }, 0.3);
    } else {
      gsap.set(this.$$('.dna-cap'), { opacity: 1, y: 0 });
    }

    // Every product draws down into the capabilities it is built on.
    const links = this.$$<SVGPathElement>('.dna-link:not(.dna-link--next)');
    if (forward && step === 2) {
      draw(links, false, { immediate: true });
      this.row.forEach((_, i) => {
        tl.add(draw(this.$$(`.dna-link[data-product="${i}"]`), true, { duration: 0.9, ease: 'power2.inOut' }), 0.2 + i * 0.14);
      });
      tl.add(revealWords(this.$('.dna-line')), 1.6);
    } else {
      draw(links, step >= 2, { immediate: true });
    }

    // The next product doesn't start from zero: it connects to everything at once.
    const nextLinks = this.$$('.dna-link--next');
    if (forward && step === 3) {
      tl.fromTo(this.$$('.dna-next circle'), { scale: 0, transformOrigin: '50% 50%' }, { scale: 1, duration: 0.7, ease: 'back.out(2)' }, 0.1);
      draw(nextLinks, false, { immediate: true });
      tl.add(draw(nextLinks, true, { duration: 0.8, ease: 'power2.out' }), 0.5);
    } else {
      gsap.set(this.$$('.dna-next circle'), { scale: 1, transformOrigin: '50% 50%' });
      draw(nextLinks, step >= 3, { immediate: true });
    }

    this.$('.dna-diagram').dataset.connected = String(step >= 2);
    this.syncFlow(step >= 2, forward && step === 2);
  }

  /** Energy moves along a sample of the connections so the foundation reads as alive. */
  private syncFlow(on: boolean, delayed: boolean): void {
    if (on === this.flowing) return;
    this.flowing = on;
    if (!on) {
      this.stopLoops();
      this.$$('.dna-pulse').forEach((m) => { m.style.opacity = '0'; });
      return;
    }
    const links = this.$$<SVGPathElement>('.dna-link:not(.dna-link--next)');
    this.$$<SVGCircleElement>('.dna-pulse').forEach((marker, i) => {
      const path = links[(i * 7) % links.length];
      this.loop(pulse(marker, path, { duration: 1.6 + (i % 3) * 0.3, delay: (delayed ? 2.4 : 0) + i * 0.35, repeatDelay: 0.8 + (i % 4) * 0.4 }));
    });
  }
}
